import { AssetCategory, AssetCategoriesResponse, AssetCategoryResponse } from '../types/assetCategory'

// API URL - using CRO Service
const getApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_CRO_API_URL
  const mode = import.meta.env.MODE

  if (envUrl) {
    return envUrl
  }

  const API_URLS = {
    local: 'http://localhost:3007/api/v1',
    remote: 'https://ath-avi-cro-uat-west.azurewebsites.net/api/v1'
  }

  return mode === 'remote' ? API_URLS.remote : API_URLS.local
}

class AssetCategoryService {
  private baseURL: string

  constructor() {
    this.baseURL = getApiUrl()
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('auth-token')
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    const token = this.getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.error?.message || errorData.message || `HTTP ${response.status}`
      )
    }
    return response.json()
  }

  /**
   * Get all asset categories for a customer
   * @param customerId - Customer ID (required)
   * @param status - Optional filter by status
   */
  async getAssetCategories(
    customerId: string,
    status?: string
  ): Promise<AssetCategory[]> {
    const url = new URL(`${this.baseURL}/asset-categories`)
    url.searchParams.append('customerId', customerId)
    if (status) {
      url.searchParams.append('status', status)
    }

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const responseData = await this.handleResponse<AssetCategoriesResponse>(response)

    return responseData.data
  }

  /**
   * Get a single asset category by ID
   * @param id - Category ID
   * @param customerId - Customer ID (required)
   */
  async getAssetCategory(id: string, customerId: string): Promise<AssetCategory> {
    const url = new URL(`${this.baseURL}/asset-categories/${id}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const responseData = await this.handleResponse<AssetCategoryResponse>(response)

    return responseData.data
  }
}

export const assetCategoryService = new AssetCategoryService()
